"use client";

import { useSyncExternalStore } from "react";

// Mirror of the contract in apps/desktop/src/bridge-contract.ts. Web deploys
// continuously while installed apps update on restart, so every member is
// optional: check for a function before calling it, and never remove or
// change one in place.

export type DesktopPrinter = {
  name: string;
  displayName: string;
};

export type DesktopPrintResult =
  | { ok: true }
  | { ok: false; reason: "no_printer" | "timeout" | "print_failed" | "busy" };

export type LapymeDesktopBridge = {
  version?: string;
  openRegister?: () => Promise<void>;
  listPrinters?: () => Promise<DesktopPrinter[]>;
  getPrinter?: () => Promise<string | null>;
  setPrinter?: (name: string | null) => Promise<void>;
  /**
   * Prints on this PC's saved printer without a dialog. The HTML must call
   * `window.print()` once it is ready; that call is the signal to print.
   */
  printHtml?: (html: string, copies: number) => Promise<DesktopPrintResult>;
  printPdf?: (bytes: Uint8Array) => Promise<DesktopPrintResult>;
  savePdf?: (
    bytes: Uint8Array,
    filename: string
  ) => Promise<
    { ok: true } | { ok: false; reason: "cancelled" | "save_failed" }
  >;
  openExternal?: (url: string) => Promise<void>;
};

type WindowWithDesktopBridge = Window & {
  lapymeDesktop?: LapymeDesktopBridge;
};

export function getDesktopBridge(): LapymeDesktopBridge | null {
  if (typeof window === "undefined") {
    return null;
  }
  return (window as WindowWithDesktopBridge).lapymeDesktop ?? null;
}

export type DesktopPrintingBridge = LapymeDesktopBridge &
  Required<
    Pick<
      LapymeDesktopBridge,
      "listPrinters" | "getPrinter" | "setPrinter" | "printHtml"
    >
  >;

export function getDesktopPrintingBridge(): DesktopPrintingBridge | null {
  const bridge = getDesktopBridge();
  if (
    typeof bridge?.listPrinters === "function" &&
    typeof bridge.getPrinter === "function" &&
    typeof bridge.setPrinter === "function" &&
    typeof bridge.printHtml === "function"
  ) {
    return bridge as DesktopPrintingBridge;
  }
  return null;
}

// The preload script defines the bridge before any page script runs and it
// never changes afterwards, so there is nothing to subscribe to.
const subscribeToNothing = () => () => undefined;

export function useIsDesktopApp(): boolean {
  return useSyncExternalStore(
    subscribeToNothing,
    () => getDesktopBridge() !== null,
    () => false
  );
}

function isWindowsBrowser(): boolean {
  if (getDesktopBridge() !== null) {
    return false;
  }
  const navigatorWithUaData = navigator as Navigator & {
    userAgentData?: { platform?: string };
  };
  const platform = navigatorWithUaData.userAgentData?.platform;
  if (platform) {
    return platform === "Windows";
  }
  return /Windows/i.test(navigator.userAgent);
}

export function useIsWindowsBrowser(): boolean {
  return useSyncExternalStore(
    subscribeToNothing,
    isWindowsBrowser,
    () => false
  );
}
